import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionRow } from "./ActionRow";
import { EnableToggle } from "./EnableToggle";
import { EditorFooter } from "./EditorFooter";
import { categoryLabelKey, isRanked } from "./playbookUtils";

/**
 * Right pane: header (category eyebrow, Customized pill, name, subLine,
 * state label + EnableToggle), body (off banner / empty / action rows +
 * add + auto-rank), footer (reset / discard / save state / Save changes).
 *
 * Keyboard reorder focus restoration (S5): a ref-map keyed by the action's
 * stable `_key` plus a pending-focus ref, restored in a post-render effect
 * — mirrors the design's `_focusGrip` + `componentDidUpdate`.
 */
export const PlaybookEditor = ({
  playbook,
  actions,
  isDirty,
  saveState,
  toggleDisabled,
  onToggle,
  onTextChange,
  onPriorityChange,
  onAdd,
  onRemove,
  onReorder,
  onAutoRank,
  onSave,
  onReset,
  onDiscard,
}) => {
  const { t } = useTranslation();
  const gripRefs = useRef({});
  const pendingFocusKey = useRef(null);
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  // Belt-and-suspenders: React's keyed reconciliation (key={action._key})
  // already tends to preserve focus across a reorder, but the grip button
  // unmounts/remounts in some paths, so we explicitly restore focus to the
  // moved row's grip after the render that applied the reorder.
  useEffect(() => {
    if (pendingFocusKey.current) {
      const key = pendingFocusKey.current;
      pendingFocusKey.current = null;
      const el = gripRefs.current[key];
      if (el) el.focus();
    }
  }, [actions]);

  if (!playbook) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-sm text-gray-400">
        {t("pages.settings.signalsPlaybooks.editor.noSelection")}
      </div>
    );
  }

  const isOff = !playbook.is_enabled;
  const isCustomized = playbook.source === "team";
  const hasActions = actions.length > 0;
  const canAutoRank = actions.length > 1 && !isRanked(actions);

  const actionCountLabel =
    actions.length === 0
      ? t("pages.settings.signalsPlaybooks.editor.noActionsYet")
      : t("pages.settings.signalsPlaybooks.editor.actionCount", {
          count: actions.length,
        });
  const originLabel = isCustomized
    ? t("pages.settings.signalsPlaybooks.editor.originCustomized")
    : t("pages.settings.signalsPlaybooks.editor.originDefault");
  const subLine = `${actionCountLabel} · ${originLabel}`;

  const leftNote = isCustomized
    ? saveState === "idle" && !isDirty
      ? t("pages.settings.signalsPlaybooks.editor.customizedNote")
      : ""
    : hasActions
      ? t("pages.settings.signalsPlaybooks.editor.inheritedNote")
      : "";

  const handleReorder = (fromIndex, toIndex, focusKey) => {
    pendingFocusKey.current = focusKey;
    onReorder(fromIndex, toIndex);
  };

  const handleDrop = (dropIndex) => {
    const fromIndex = actions.findIndex((a) => a._key === dragKey);
    if (fromIndex > -1 && fromIndex !== dropIndex) {
      onReorder(fromIndex, dropIndex);
    }
    setDragKey(null);
    setOverKey(null);
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col bg-white">
      <div className="border-b border-gray-200 p-5 pb-4">
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {t(
                  `pages.settings.signalsPlaybooks.categories.${categoryLabelKey(playbook.category)}`,
                )}
              </span>
              {isCustomized && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                  {t("pages.settings.signalsPlaybooks.customizedTag")}
                </span>
              )}
            </div>
            <h3 className="mb-1 mt-1.5 text-lg font-bold text-gray-900">
              {playbook.display_name}
            </h3>
            <p className="text-xs text-gray-400">{subLine}</p>
          </div>
          <div className="flex flex-none items-center gap-2.5 pt-1">
            <span
              className={`text-xs font-medium ${isOff ? "text-gray-400" : "text-green-600"}`}
            >
              {isOff
                ? t("pages.settings.signalsPlaybooks.editor.stateOff")
                : t("pages.settings.signalsPlaybooks.editor.stateOn")}
            </span>
            <EnableToggle
              isEnabled={playbook.is_enabled}
              disabled={toggleDisabled}
              onChange={onToggle}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {isOff && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
            <span className="h-1.5 w-1.5 flex-none rounded-full bg-gray-400" />
            <span>{t("pages.settings.signalsPlaybooks.editor.offBanner")}</span>
          </div>
        )}

        {!hasActions && (
          <div className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-5 py-6 text-center">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-lg text-gray-400">
              +
            </div>
            <div className="text-sm font-semibold text-gray-700">
              {t("pages.settings.signalsPlaybooks.editor.emptyTitle")}
            </div>
            <div className="max-w-xs text-xs text-gray-400">
              {t("pages.settings.signalsPlaybooks.editor.emptyBody")}
            </div>
            <button
              type="button"
              onClick={onAdd}
              className="mt-1.5 rounded-lg border border-blue-600 bg-blue-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              {t("pages.settings.signalsPlaybooks.editor.addFirstAction")}
            </button>
          </div>
        )}

        {hasActions && (
          <div className="flex flex-col gap-2">
            {actions.map((action, index) => (
              <ActionRow
                key={action._key}
                action={action}
                isOff={isOff}
                gripRef={(el) => {
                  if (el) gripRefs.current[action._key] = el;
                  else delete gripRefs.current[action._key];
                }}
                onTextChange={(text) => onTextChange(index, text)}
                onPriorityChange={(priority) =>
                  onPriorityChange(index, priority)
                }
                onRemove={() => onRemove(index)}
                isDragSource={dragKey === action._key}
                isDragOver={
                  overKey === action._key &&
                  dragKey != null &&
                  dragKey !== action._key
                }
                onDragStart={() => setDragKey(action._key)}
                onDragEnd={() => {
                  setDragKey(null);
                  setOverKey(null);
                }}
                onDragOver={() => setOverKey(action._key)}
                onDrop={() => handleDrop(index)}
                onGripKeyDown={(e) => {
                  if (e.key === "ArrowUp" && index > 0) {
                    e.preventDefault();
                    handleReorder(index, index - 1, action._key);
                  } else if (
                    e.key === "ArrowDown" &&
                    index < actions.length - 1
                  ) {
                    e.preventDefault();
                    handleReorder(index, index + 1, action._key);
                  }
                }}
              />
            ))}

            <div className="mt-0.5 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={onAdd}
                className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-2.5 py-1.5 text-xs text-gray-600 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900"
              >
                <span className="text-sm leading-none">+</span>
                {t("pages.settings.signalsPlaybooks.editor.addAction")}
              </button>
              {canAutoRank && (
                <button
                  type="button"
                  onClick={onAutoRank}
                  title={t(
                    "pages.settings.signalsPlaybooks.editor.autoRankTitle",
                  )}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                >
                  <span className="inline-flex items-center gap-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-rose-600" />
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-600" />
                    <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
                  </span>
                  {t("pages.settings.signalsPlaybooks.editor.autoRank")}
                </button>
              )}
            </div>
          </div>
        )}

        <EditorFooter
          showReset={isCustomized}
          showDiscard={isDirty}
          leftNote={leftNote}
          saveState={saveState}
          isDirty={isDirty}
          onReset={onReset}
          onDiscard={onDiscard}
          onSave={onSave}
        />
      </div>
    </div>
  );
};
